import { cn } from '@/internationalization/cn'
import { initialsOf } from '@/internationalization/format'
import { speakerColorVar } from '@/internationalization/speaker-color'

export interface AvatarProps {
  name: string
  src?: string | null
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Green ring for someone currently in the meeting. */
  isLive?: boolean
  /** Use the speaker palette so an avatar matches its transcript colour. */
  useSpeakerColor?: boolean
  className?: string
}

const SIZES = {
  xs: 'h-5 w-5 text-2xs',
  sm: 'h-7 w-7 text-2xs',
  md: 'h-9 w-9 text-xs',
  lg: 'h-12 w-12 text-base',
} as const

export function Avatar({ name, src, size = 'md', isLive, useSpeakerColor, className }: AvatarProps) {
  const token = speakerColorVar(name)
  return (
    <span
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full font-semibold uppercase',
        'ring-1 ring-inset ring-black/5',
        !useSpeakerColor && 'bg-surface-muted text-fg-muted',
        SIZES[size],
        className,
      )}
      style={
        useSpeakerColor
          ? { backgroundColor: `hsl(var(${token}) / 0.16)`, color: `hsl(var(${token}))` }
          : undefined
      }
    >
      {src ? (
        <img src={src} alt="" className="h-full w-full rounded-full object-cover" />
      ) : (
        <span aria-hidden>{initialsOf(name)}</span>
      )}
      <span className="sr-only">{name}</span>
      {isLive && (
        <span
          aria-hidden
          className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success ring-2 ring-surface"
        />
      )}
    </span>
  )
}

export interface AvatarGroupProps {
  people: { displayName: string; avatarUrl?: string | null }[]
  max?: number
  size?: AvatarProps['size']
  className?: string
}

/** Overlapping avatars with a "+N" overflow chip. Order is caller's business. */
export function AvatarGroup({ people, max = 4, size = 'sm', className }: AvatarGroupProps) {
  const shown = people.slice(0, max)
  const overflow = people.length - shown.length
  return (
    <span className={cn('flex items-center', className)}>
      {shown.map((person, index) => (
        <Avatar
          key={`${person.displayName}-${index}`}
          name={person.displayName}
          src={person.avatarUrl}
          size={size}
          useSpeakerColor
          className="-ml-1.5 ring-2 ring-surface first:ml-0"
        />
      ))}
      {overflow > 0 && (
        <span
          className={cn(
            '-ml-1.5 inline-flex items-center justify-center rounded-full bg-surface-muted',
            'font-semibold text-fg-muted ring-2 ring-surface',
            SIZES[size],
          )}
        >
          +{overflow}
        </span>
      )}
    </span>
  )
}
